import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    sameChannel,
    type BanEntry, type ChatMessage, type ErrorCode, type Participant, type PublicConfig,
    type Publication, type RoomState, type ServerMessage, type ShareKind
} from '@shared/protocol'
import { SignalingClient, defaultSignalingUrl, type SocketStatus } from '@/lib/signalingClient'
import {
    DEFAULT_QUALITY, DEFAULT_RESOLUTION, QUALITY_PRESETS, captureCamera, captureScreen, defaultQuality,
    screenAudioMissingMessage, sizeConstraints, stopStream, trackOrientation, uploadBudget,
    type ShareQuality, type ShareResolution, type SourceSize
} from '@/lib/screenCapture'
import { createSfuApi } from '@/lib/sfuClient'
import { SfuTransport } from '@/lib/sfuTransport'
import {
    createMeshTransport,
    type LocalStream, type PeerConnectionState, type StreamTransport
} from '@/lib/transport'
import { loadNickname, roomCodeFromLocation, saveNickname, saveLastRoomCode } from '@/lib/format'
import { bitrateBetween, formatBitrate, type TrackSample } from '@/lib/streamQuality'
import { loadSavedAvatar, saveUserAvatar, removeUserAvatar } from '@/lib/avatar'
import {
    applyNativeBlur, createBlurPipeline,
    type BackgroundEffect, type BlurPipeline
} from '@/lib/videoEffects'
import { captureMicrophone, createVoicePipeline, type VoicePipeline } from '@/lib/voiceCapture'
import {
    detectHardwareEncoders, loadEncoderChoice, resolveEncoder, saveEncoderChoice,
    type EncoderChoice, type ShareEncoder
} from '@/lib/videoEncoder'
import {
    loadReceivePreference, loosestLimit, saveReceivePreference, type ReceiveLimit, type ReceivePreference
} from '@/lib/receiveQuality'

export type SessionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting'

export interface SessionError {
    code: ErrorCode | 'capture_failed' | 'socket_failed' | 'replaced' | 'channel_closed' | 'screen_no_audio'
    message: string
    /**
     * `aviso` e o que nao quebrou nada.
     *
     * A tela sem som e o caso que criou esta distincao: a transmissao subiu,
     * esta no ar e funcionando — o que falta e uma caixinha no seletor do
     * navegador. Pintar isso de vermelho, do lado de "nao consegui capturar a
     * tela", faria a pessoa achar que precisa tentar de novo.
     */
    severity?: 'erro' | 'aviso'
}

/**
 * Erros que encerram a presenca na sala mesmo com ela de pe. Qualquer outro
 * erro que chega com a sala conectada e a recusa de uma acao (criar sub-sala
 * com nome repetido, moderar sem cargo, mensagem demais) — a sala continua, e
 * a intencao de reconectar tambem.
 */
const ERROS_QUE_TIRAM_DA_SALA: ReadonlySet<ErrorCode> = new Set(['kicked', 'room_closed'])

/**
 * Uma transmissao sua no ar.
 *
 * `stream` e o que vai pros outros; `raw` guarda a camera de verdade quando o
 * efeito de fundo roda por fora dela. Sao duas coisas separadas porque fechar
 * so a publicada deixaria a webcam acesa.
 */
export interface LocalCapture {
    kind: ShareKind
    stream: MediaStream
    raw: MediaStream | null
    pipeline: BlurPipeline | null
    /** Cadeia de audio do microfone, quando a supressao por ML esta ligada. */
    voice?: VoicePipeline | null
}

/** De quanto em quanto tempo fps e bitrate sao remedidos. */
const STATS_INTERVAL_MS = 2_000

const FALLBACK_CONFIG: PublicConfig = {
    iceServers: [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] }],
    maxParticipants: 12,
    brand: 'MRI Qbox',
    mode: 'mesh',
    // Ligado no fallback: se o `/api/config` nao respondeu, a sala funciona
    // igual e o pior caso e uma medida a mais de vez em quando.
    streamStats: true
}

/** Chave das streams que chegam: o par identifica a transmissao exata. */
export function streamKey(peerId: string, streamId: string): string {
    return `${peerId}::${streamId}`
}

/**
 * A maior fonte de video no ar, ou null sem nenhuma.
 *
 * O orcamento de upload e um so pro transporte inteiro, e quem o consome de
 * verdade e a fonte maior — quase sempre a tela. `raw` primeiro porque e ele
 * que carrega o tamanho real da camera quando ha efeito de fundo.
 */
function largestSource(captures: LocalCapture[]): SourceSize | null {
    let largest: SourceSize | null = null
    for (const capture of captures) {
        for (const track of (capture.raw ?? capture.stream).getVideoTracks()) {
            const { width = 0, height = 0 } = track.getSettings?.() ?? {}
            if (width * height > (largest ? largest.width * largest.height : 0)) largest = { width, height }
        }
    }
    return largest
}

/**
 * Duas rodadas de banda mostram a mesma coisa?
 *
 * Compara pelo texto da etiqueta, e nao pelo numero cru: o bitrate medido
 * quase nunca repete ao bit, e "4.2 Mbps" que vira "4.2 Mbps" nao merece
 * re-renderizar a sala.
 */
function sameRates(a: Record<string, number>, b: Record<string, number>): boolean {
    const keys = Object.keys(a)
    if (keys.length !== Object.keys(b).length) return false
    return keys.every((key) => key in b && formatBitrate(a[key]!) === formatBitrate(b[key]!))
}

const SESSION_STORAGE_KEY = 'mri_active_session'

interface ActiveSessionData {
    code: string
    token: string
    selfId: string
    savedAt: number
}

function saveActiveSession(code: string, token: string, selfId: string): void {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
            code,
            token,
            selfId,
            savedAt: Date.now()
        }))
    } catch {
        // Nada
    }
}

/**
 * Por quanto tempo o token guardado ainda faz a aba voltar sozinha pra sala.
 *
 * Contado do ultimo sinal de vida da aba, nao da entrada na sala: quem sai da
 * pagina renova o relogio antes de ir. Passado isso, abrir o endereco de uma
 * sala volta a ser uma entrada comum, pela tela inicial.
 *
 * Bem maior que o `BYE_HOLD_SECONDS` do servidor de proposito: reassumir o
 * MESMO id so cabe naqueles dois segundos, mas voltar pra sala sem ter que
 * digitar o nickname de novo vale por muito mais tempo.
 */
const RESUME_WINDOW_MS = 60_000

/** Renova o relogio do token sem precisar do codigo nem do id. */
function touchActiveSession(): void {
    try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
        if (!raw) return
        const data = JSON.parse(raw) as ActiveSessionData
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ ...data, savedAt: Date.now() }))
    } catch {
        // Nada
    }
}

function loadActiveSession(code: string): string | null {
    try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
        if (!raw) return null
        const data = JSON.parse(raw) as ActiveSessionData
        if (data.code === code && Date.now() - data.savedAt < RESUME_WINDOW_MS) {
            return data.token
        }
    } catch {
        return null
    }
    return null
}

function clearActiveSession(): void {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch {
        // Nada
    }
}

/**
 * Estado completo de uma sala: socket de sinalizacao, lista de participantes,
 * transporte de midia (malha P2P ou SFU) e o que voce publica. A UI so consome
 * o que sai daqui.
 *
 * Uma pessoa pode ter tela e camera no ar ao mesmo tempo — sao publicacoes
 * independentes, cada uma com seu tile.
 */
export function useRoomSession() {
    const [status, setStatus] = useState<SessionStatus>('idle')
    const [selfId, setSelfId] = useState<string | null>(null)
    const [room, setRoom] = useState<RoomState | null>(null)
    /**
     * Quem foi expulso desta sala. So chega a quem modera — pra convidado a
     * lista fica vazia porque o servidor nunca a manda.
     */
    const [bans, setBans] = useState<BanEntry[]>([])
    /** Bate-papo da sala. Vive enquanto a sala vive — nada e guardado. */
    const [messages, setMessages] = useState<ChatMessage[]>([])
    /**
     * Muda toda vez que a conversa e trocada inteira por um historico (ao
     * entrar, ou ao trocar de sub-sala). E o que separa "chegou o que ja tinha
     * sido dito" de "chegou mensagem nova" pra quem conta nao lidas e toca o
     * aviso — pelo tamanho da lista nao da pra saber.
     */
    const [historyVersion, setHistoryVersion] = useState(0)
    /** Streams que chegam, por `streamKey(peerId, streamId)`. */
    const [streams, setStreams] = useState<Record<string, MediaStream>>({})
    /**
     * Como esta a conexao de midia com cada peer.
     *
     * Vale pros dois transportes: mesh e SFU reportam o mesmo vocabulario, e e
     * isso que permite a UI mostrar "conectando" ou "nao vai conectar" sem
     * saber qual caminho de midia esta no ar. Sem este estado, falha de ICE
     * (NAT simetrico sem TURN, por exemplo) era tile preto sem explicacao.
     *
     * Sai daqui por prop, descendo pelo palco ate o tile.
     */
    const [peerStates, setPeerStates] = useState<Record<string, PeerConnectionState>>({})
    /**
     * Bits por segundo por id de trilha de video.
     *
     * Por id de trilha, e nao por tile: e a unica chave que o transporte sabe
     * produzir sem conhecer a UI, e a UI ja tem a `MediaStream` na mao pra
     * fazer a ponte.
     *
     * Fica vazio quando `streamStats` esta desligado no servidor — e tambem
     * quando nao ha conexao de midia nenhuma, que e o caso de estar sozinho na
     * sala em modo mesh. Ali nao ha banda a medir porque nao ha nada sendo
     * enviado; resolucao e fps continuam aparecendo, os dois saem do `<video>`.
     */
    const [bitrates, setBitrates] = useState<Record<string, number>>({})
    /**
     * Quem esta de fato codificando o que eu envio, segundo o navegador.
     *
     * Existe porque pedir a GPU nao garante a GPU: sem encoder de hardware (ou
     * com as sessoes da placa esgotadas, na malha) o Chrome cai pro software
     * calado. `null` quando nao ha o que medir ou o navegador nao conta.
     */
    const [encoderInUse, setEncoderInUse] = useState<ShareEncoder | null>(null)
    /** O navegador tem encoder de placa pra H.264? `null` = nao sabe dizer. */
    const [gpuAvailable, setGpuAvailable] = useState<boolean | null>(null)
    const gpuAvailableRef = useRef<boolean | null>(null)
    /** Suas transmissoes no ar (no maximo uma de cada tipo). */
    const [captures, setCaptures] = useState<LocalCapture[]>([])
    const [cameraId, setCameraIdState] = useState<string | null>(null)
    /** Fundo da camera: nenhum, desfocado ou imagem. Tela nunca leva efeito. */
    const [background, setBackgroundState] = useState<BackgroundEffect>({ kind: 'none' })
    const [effectLoading, setEffectLoading] = useState(false)
    /** Microfone aberto mas silenciado — continua na voz, so nao manda audio. */
    const [muted, setMutedState] = useState(false)
    const [micId, setMicIdState] = useState<string | null>(null)
    /** Supressao de ruido por ML (RNNoise) alem da nativa do navegador. */
    const [noiseSuppression, setNoiseSuppressionState] = useState(true)
    const [voiceLoading, setVoiceLoading] = useState(false)
    /**
     * O audio da tela que estou mandando esta cortado?
     *
     * Cortar aqui e desligar a trilha (`enabled = false`), nao remove-la: a
     * conexao segue igual e o outro lado recebe silencio, sem renegociacao
     * nenhuma. Tirar a trilha faria a malha inteira renegociar a cada clique.
     */
    const [screenAudioMuted, setScreenAudioMuted] = useState(false)
    const [quality, setQuality] = useState<ShareQuality>(DEFAULT_QUALITY)
    /**
     * Teto de resolucao escolhido por quem transmite.
     *
     * Vive ao lado da qualidade porque os dois juntos e que definem o custo da
     * transmissao — a qualidade escolhe a cadencia, a resolucao escolhe o
     * tamanho, e o orcamento de banda sai da combinacao.
     */
    const [resolution, setResolution] = useState<ShareResolution>(DEFAULT_RESOLUTION)
    const resolutionRef = useRef<ShareResolution>(DEFAULT_RESOLUTION)
    const qualityRef = useRef<ShareQuality>(DEFAULT_QUALITY)
    /**
     * A pessoa escolheu a qualidade no menu nesta sessao? Ate escolher, a
     * qualidade acompanha o encoder (`defaultQuality`): 60 fps com a GPU, 30
     * no processador.
     */
    const qualityChosenRef = useRef(false)
    /**
     * Placa de video ou processador codificando o video que sai daqui.
     *
     * Terceiro eixo do custo, e o unico que nao mexe na imagem: qualidade e
     * resolucao decidem QUANTO codificar, este decide ONDE. A escolha do menu
     * e lembrada entre visitas porque e uma caracteristica da maquina, nao da
     * sala; `encoder` e o que ela resolve agora (no automatico, depende da
     * deteccao da placa).
     */
    const [encoderChoice, setEncoderChoice] = useState<EncoderChoice>(loadEncoderChoice)
    const encoderChoiceRef = useRef<EncoderChoice>(encoderChoice)
    const [encoder, setEncoderState] = useState<ShareEncoder>(() => resolveEncoder(encoderChoice, null))
    const encoderRef = useRef<ShareEncoder>(encoder)
    /** How much of the others' streams this machine wants to receive. */
    const [receivePreference, setReceivePreference] = useState<ReceivePreference>(loadReceivePreference)
    const [error, setError] = useState<SessionError | null>(null)
    const [config, setConfig] = useState<PublicConfig>(FALLBACK_CONFIG)
    const [avatar, setAvatarState] = useState<string | null>(() => loadSavedAvatar())

    const clientRef = useRef<SignalingClient | null>(null)
    const transportRef = useRef<StreamTransport | null>(null)
    const avatarRef = useRef<string | null>(avatar)
    /** Espelho de `captures` pra quem roda fora do render (callbacks, timers). */
    const capturesRef = useRef<LocalCapture[]>([])
    /**
     * Serializa publicacoes: duas ao mesmo tempo corrompem o SDP e travam
     * a conexao. Cada chamada encadeia na promise anterior.
     */
    const publishLock = useRef<Promise<void>>(Promise.resolve())
    /**
     * Camera escolhida. Vai em ref alem do estado porque trocar de camera
     * reabre a captura no mesmo clique — e o `setState` so valeria no render
     * seguinte, fazendo a captura pegar o dispositivo antigo.
     */
    const cameraIdRef = useRef<string | null>(null)
    const backgroundRef = useRef<BackgroundEffect>({ kind: 'none' })
    const micIdRef = useRef<string | null>(null)
    const noiseSuppressionRef = useRef(true)
    const mutedRef = useRef(false)
    /**
     * Token da sala. Serve pras rotas do SFU e, na volta de uma queda, pra
     * reassumir o proprio lugar em vez de entrar como gente nova.
     */
    const tokenRef = useRef<string | null>(null)
    /** Id atual: comparado depois do join pra saber se a retomada funcionou. */
    const selfIdRef = useRef<string | null>(null)
    /**
     * Guardado pra refazer o join sozinho quando o socket cair. O PIN vai
     * junto: sem ele, reconectar numa sala protegida cairia em `pin_required`
     * e a pessoa teria que digitar de novo a cada oscilacao de rede.
     */
    const intentRef = useRef<{ code: string | null; nickname: string; pin?: string } | null>(null)
    const configRef = useRef<PublicConfig>(FALLBACK_CONFIG)
    /** Espelho de `status` pros handlers de mensagem, que nao re-renderizam. */
    const statusRef = useRef<SessionStatus>('idle')
    /**
     * Sub-sala em que estou agora, segundo o servidor. Os handlers precisam
     * dela pra perceber que a sub-sala em que eu estava foi apagada.
     */
    const selfChannelRef = useRef<string | null>(null)
    /**
     * Sub-sala que EU escolhi, e de qual sala.
     *
     * A retomada devolve o mesmo lugar, mas quando ela nao pega (deploy que
     * demorou mais que a carencia) a pessoa volta como gente nova, na sala
     * principal. Com isto ela volta sozinha pra sub-sala em que estava, em vez
     * de a sub-sala inteira se espalhar pela principal a cada atualizacao.
     */
    const desiredChannelRef = useRef<{ code: string; channel: string } | null>(null)
    /**
     * `stopSharing` nasce depois do handler de mensagens (depende da cadeia de
     * captura inteira); o handler chega nele por aqui.
     */
    const stopSharingRef = useRef<() => void>(() => undefined)

    useEffect(() => {
        let cancelled = false
        fetch('/api/config')
            .then((response) => (response.ok ? response.json() : null))
            .then((data: PublicConfig | null) => {
                if (cancelled || !data) return
                configRef.current = data
                setConfig(data)
            })
            .catch(() => {
                // Sem /api/config o STUN publico do fallback ja resolve LAN e a maioria dos NATs.
            })
        return () => { cancelled = true }
    }, [])

    const putStream = useCallback((peerId: string, streamId: string, stream: MediaStream) => {
        // Sem atalho por identidade aqui. `ontrack` dispara uma vez por TRILHA e
        // entrega a MESMA MediaStream nas duas (o video da tela chega primeiro,
        // o audio dela logo depois). Comparar `===` e sair fazia a trilha de
        // audio entrar sem ninguem re-renderizar — e o botao de som do tile,
        // decidido no render, nunca aparecia. Sao duas ou tres chamadas por
        // transmissao: re-renderizar em todas nao custa nada.
        setStreams((current) => ({ ...current, [streamKey(peerId, streamId)]: stream }))
    }, [])

    /** Peer sumiu: leva junto tudo o que ele publicava. */
    const dropPeer = useCallback((peerId: string) => {
        setStreams((current) => {
            const prefix = `${peerId}::`
            const keys = Object.keys(current).filter((key) => key.startsWith(prefix))
            if (keys.length === 0) return current
            const next = { ...current }
            for (const key of keys) delete next[key]
            return next
        })
    }, [])

    /**
     * Estado da conexao com um peer, vindo do transporte ativo.
     *
     * `closed` some do mapa em vez de virar entrada: peer fechado nao tem tile,
     * e guardar o ultimo estado dele deixaria um aviso velho na tela se ele
     * voltasse a transmitir.
     */
    const putPeerState = useCallback((peerId: string, state: PeerConnectionState) => {
        setPeerStates((current) => {
            if (state === 'closed') {
                if (!(peerId in current)) return current
                const next = { ...current }
                delete next[peerId]
                return next
            }
            if (current[peerId] === state) return current
            return { ...current, [peerId]: state }
        })
    }, [])

    /**
     * Orcamento de upload pro que esta no ar agora.
     *
     * Le o tamanho real das fontes, e nao so o preset: em `nativa` e so a
     * captura que sabe se a tela e 1080p ou 4K, e o orcamento de uma serve mal
     * a outra. Por isso e recalculado sempre que uma captura entra, sai ou muda
     * de tamanho.
     */
    const currentBudget = useCallback(() => uploadBudget(
        qualityRef.current,
        resolutionRef.current,
        largestSource(capturesRef.current)
    ), [])

    const ensureTransport = useCallback((id: string) => {
        if (transportRef.current) return transportRef.current
        const current = configRef.current

        const transport: StreamTransport = current.mode === 'sfu'
            ? new SfuTransport({
                api: createSfuApi(() => tokenRef.current),
                iceServers: current.iceServers,
                onStream: putStream,
                onPeerGone: dropPeer,
                onPeerState: putPeerState,
                // Sem isso o transporte nunca sabe reportar a propria publicacao
                // pelo `onPeerState` — o id era so usado pra mesh, e o SFU ficava
                // sem chave pra avisar quando a publicacao de saida cai.
                selfId: id
            })
            : createMeshTransport({
                selfId: id,
                iceServers: current.iceServers,
                sendSignal: (to, data) => clientRef.current?.send({ t: 'signal', to, data }),
                onStream: putStream,
                onPeerGone: dropPeer,
                onPeerState: putPeerState
            })

        transport.setEncoder(encoderRef.current)
        transport.setMaxBitrate(currentBudget())
        transportRef.current = transport
        return transport
    }, [putStream, dropPeer, putPeerState, currentBudget])

    const teardownTransport = useCallback(() => {
        transportRef.current?.close()
        transportRef.current = null
        setStreams({})
        setPeerStates({})
    }, [])

    /**
     * Manda pro transporte tudo o que esta capturado e anuncia pra sala.
     *
     * Uma chamada so pro conjunto inteiro: o transporte precisa ver todas as
     * transmissoes juntas pra renegociar uma vez, e o anuncio precisa sair
     * completo — lista parcial faria os outros pararem de assinar o que ficou
     * de fora.
     *
     * Serializada: se outra chamada estiver em andamento, esta espera. Sem
     * isso, duas publicacoes simultaneas corrompem o estado do SDP e travam
     * a conexao (race condition).
     */
    const publishAll = useCallback(async (transport: StreamTransport | null) => {
        const job = publishLock.current.then(async () => {
            const current = capturesRef.current
            const locals: LocalStream[] = current.map(({ kind, stream }) => ({ kind, stream }))
            const sfu = (await transport?.setLocalStreams(locals)) ?? new Map()

            // A pessoa pode ter parado enquanto a publicacao ia e voltava.
            if (capturesRef.current !== current) return

            const publications: Publication[] = current.map(({ kind, stream }) => ({
                kind,
                streamId: stream.id,
                ...(sfu.get(kind) ? { sfu: sfu.get(kind) } : {})
            }))
            clientRef.current?.send({ t: 'publish', publications })
        }).catch((error) => {
            console.warn('[session] publicacao falhou', error)
        })
        publishLock.current = job
        await job
    }, [])

    const handleMessage = useCallback((message: ServerMessage) => {
        switch (message.t) {
            case 'joined': {
                // Mesmo id = o servidor guardou o lugar durante a queda. Ai o
                // transporte continua de pe e ninguem na sala viu nada.
                const retomou = selfIdRef.current === message.selfId && !!transportRef.current
                if (!retomou) {
                    teardownTransport()
                    // Entrada de verdade (nao retomada): o que estiver na tela e
                    // de outra sala. O `chat-history` e o `bans` repovoam logo
                    // em seguida, quando houver o que mostrar — e o servidor so
                    // manda `bans` se houver algum, entao sem limpar aqui a
                    // lista de expulsos da sala anterior ficava na tela, com
                    // botao de liberar que nao libera ninguem.
                    setMessages([])
                    setBans([])
                }

                tokenRef.current = message.token
                selfIdRef.current = message.selfId
                saveActiveSession(message.room.code, message.token, message.selfId)
                saveLastRoomCode(message.room.code)
                setSelfId(message.selfId)
                setRoom(message.room)
                setStatus('connected')
                // Na hora, e nao so no efeito: a resposta ao `channel-join`
                // logo abaixo pode chegar antes do proximo render, e um erro
                // nela seria tratado como erro de entrada.
                statusRef.current = 'connected'
                setError(null)
                // O PIN da intencao anterior tem que sobreviver a esta reescrita:
                // e ele que o `handleStatus` reenvia no proximo rejoin, e sem ele
                // reconectar numa sala protegida depois de a carencia expirar cai
                // em `pin_required` e manda a pessoa pra tela inicial digitar de
                // novo — exatamente o que guardar o PIN na intencao existe pra
                // evitar. Lido antes da atribuicao pra nao depender da ordem de
                // avaliacao do literal.
                const pinAtual = intentRef.current?.pin
                intentRef.current = {
                    code: message.room.code,
                    nickname: message.room.participants.find((p) => p.id === message.selfId)?.nickname ?? '',
                    ...(pinAtual ? { pin: pinAtual } : {})
                }
                const transport = ensureTransport(message.selfId)

                // Voltou como gente nova (a retomada nao pegou) e caiu na sala
                // principal: pede de volta a sub-sala em que estava. Vai ANTES
                // da publicacao, entao o servidor ja a coloca la quando ela
                // reafirmar a tela — e nada do que ela transmite chega a quem
                // esta na principal.
                const desejada = desiredChannelRef.current
                const eu = message.room.participants.find((p) => p.id === message.selfId)
                if (
                    desejada
                    && desejada.code === message.room.code
                    && eu?.channel !== desejada.channel
                    && message.room.channels?.some((channel) => channel.id === desejada.channel)
                ) {
                    clientRef.current?.send({ t: 'channel-join', id: desejada.channel })
                }

                // Reconectou (ou reentrou): reafirma o que esta no ar. Sem isso
                // a UI diz que voce transmite pra uma sala que nao sabe disso —
                // ninguem ve nada e o botao fica em "Parar".
                //
                // Sempre, inclusive com a lista vazia: numa retomada de aba
                // recarregada o servidor ainda anuncia a tela e a camera de
                // antes do F5, e sem este `publish` vazio elas ficam de tile
                // fantasma em "Conectando..." pra sala inteira.
                void publishAll(transport)
                return
            }

            case 'peer-join': {
                setRoom((current) => {
                    if (!current) return current
                    // Upsert, nao append: quem volta de uma queda tambem chega
                    // como `peer-join`, e quem ja tinha a pessoa na lista nao
                    // pode ganhar uma copia dela.
                    const conhecido = current.participants.some((p) => p.id === message.peer.id)
                    return {
                        ...current,
                        participants: conhecido
                            ? current.participants.map((p) => (p.id === message.peer.id ? message.peer : p))
                            : [...current.participants, message.peer]
                    }
                })
                return
            }

            case 'peer-leave': {
                dropPeer(message.id)
                putPeerState(message.id, 'closed')
                setRoom((current) => (current
                    ? { ...current, participants: current.participants.filter((p) => p.id !== message.id) }
                    : current))
                return
            }

            case 'peer-update': {
                setRoom((current) => (current
                    ? {
                        ...current,
                        participants: current.participants.map((p) => (p.id === message.peer.id ? message.peer : p))
                    }
                    : current))
                return
            }

            case 'peer-audio': {
                setRoom((current) => (current
                    ? {
                        ...current,
                        participants: current.participants.map((p) => (p.id === message.id
                            ? { ...p, muted: message.muted, deafened: message.deafened }
                            : p))
                    }
                    : current))
                return
            }

            case 'room-update': {
                // `name` e opcional no `room-update`: sobrescrever direto
                // apagava o nome da sala fixa da tela assim que alguem
                // trancasse a porta.
                setRoom((current) => (current
                    ? { ...current, locked: message.locked, name: message.name ?? current.name }
                    : current))
                return
            }

            case 'bans': {
                setBans(message.entries)
                return
            }

            case 'channels': {
                setRoom((current) => (current ? { ...current, channels: message.channels } : current))

                /**
                 * A sub-sala em que eu estava sumiu da lista: um moderador a
                 * apagou e o servidor esta me levando pra sala principal.
                 *
                 * Microfone, tela e camera eram pra quem estava la — nao pra
                 * sala inteira. Desligar AQUI, antes do `peer-update` que me
                 * muda de lugar (o servidor manda a lista primeiro), e o que
                 * garante que a malha nao reconecte com a principal levando o
                 * que estava no ar.
                 */
                const minha = selfChannelRef.current
                if (minha && !message.channels.some((channel) => channel.id === minha)) {
                    desiredChannelRef.current = null
                    stopSharingRef.current()
                    setError({
                        code: 'channel_closed',
                        message: 'A sub-sala foi apagada. Voce voltou pra sala principal com microfone e transmissoes desligados.'
                    })
                }
                return
            }

            case 'chat': {
                setMessages((current) => [...current, message.message])
                return
            }

            case 'chat-history': {
                // Chega logo depois do `joined` (ou de trocar de sub-sala),
                // entao substitui em vez de somar: o que estiver aqui e de uma
                // sala, ou sub-sala, anterior.
                setMessages(message.messages)
                setHistoryVersion((version) => version + 1)
                return
            }

            case 'signal': {
                transportRef.current?.handleSignal(message.from, message.data)
                return
            }

            case 'error': {
                setError({ code: message.code, message: message.message })
                // Com a sala de pe, o erro e a recusa de uma acao la dentro
                // (flood no chat, sub-sala com nome repetido, moderar sem
                // cargo): a conexao segue firme, entao o status nao pode
                // fingir que caiu — e derrubar a intencao deixaria a pessoa
                // sem reconexao na proxima oscilacao de rede, por causa de um
                // clique recusado.
                if (statusRef.current === 'connected' && !ERROS_QUE_TIRAM_DA_SALA.has(message.code)) return
                // Erro de entrada derruba a intencao: nao adianta reconectar em loop.
                // `pin_required`/`invalid_pin` nao derrubam a intencao: a pessoa
                // vai digitar o PIN e tentar de novo com o mesmo codigo.
                const recuperavel = message.code === 'rate_limited'
                    || message.code === 'bad_message'
                    || message.code === 'pin_required'
                    || message.code === 'invalid_pin'
                if (!recuperavel) {
                    intentRef.current = null
                }
                // Devolve o controle pra pessoa em qualquer erro. Antes, `rate_limited`
                // e `bad_message` ficavam de fora e deixavam o status preso em
                // "connecting"/"reconnecting" pra sempre — nada reenvia a intencao
                // sozinho nesse caminho, entao so o F5 destrancava a tela inicial.
                setStatus('idle')
                if (message.code === 'kicked') {
                    desiredChannelRef.current = null
                    teardownTransport()
                    setRoom(null)
                    setBans([])
                    setMessages([])
                    setSelfId(null)
                    tokenRef.current = null
                    selfIdRef.current = null
                }
                return
            }

            default:
                return
        }
    }, [ensureTransport, publishAll, dropPeer, putPeerState, teardownTransport])

    // A malha (ou as assinaturas do SFU) seguem a lista de participantes: um
    // lugar so decide quem entra, quem sai e quem comecou a transmitir.
    //
    // So quem esta na MESMA sub-sala: e isso que faz a voz, a tela e a camera
    // ficarem dentro dela. Trocar de sub-sala e so esta lista mudar — a malha
    // fecha a conexao com quem ficou pra tras e abre com quem esta no lugar
    // novo, levando junto o que estiver no ar.
    useEffect(() => {
        if (!room || !selfId) return
        const eu = room.participants.find((participant) => participant.id === selfId) ?? {}
        transportRef.current?.syncPeers(room.participants.filter((participant) => (
            participant.id !== selfId && sameChannel(participant, eu)
        )))
    }, [room, selfId])

    useEffect(() => {
        statusRef.current = status
    }, [status])

    const handleStatus = useCallback((socketStatus: SocketStatus) => {
        const intent = intentRef.current
        if (socketStatus === 'open') {
            if (!intent) return
            // O transporte NAO e derrubado aqui: se a retomada der certo, o id
            // continua o mesmo e as conexoes P2P que os outros tem com voce
            // seguem valendo. Quem decide derrubar e o `joined`, comparando o
            // id que voltou.
            const resumeToken = tokenRef.current || (intent.code ? loadActiveSession(intent.code) : null)
            /**
             * A foto tem que vir junto aqui tambem.
             *
             * Este e o caminho NORMAL de entrada, nao so o de reconexao: quando
             * a pessoa clica em entrar, o socket quase sempre ainda esta
             * abrindo, o `send` do `enter()` falha e quem manda o `join` de
             * verdade e este trecho. Sem a foto aqui, ela ficava so na tela de
             * quem a escolheu — o servidor nunca a recebia, entao nao aparecia
             * na lista de participantes, no bate-papo nem pros outros.
             *
             * Lida do ref, e nao da intencao: e o valor de agora, inclusive se
             * a pessoa trocou a foto enquanto a rede estava caida.
             */
            const userAvatar = avatarRef.current || undefined
            clientRef.current?.send(intent.code
                ? {
                    t: 'join',
                    code: intent.code,
                    nickname: intent.nickname,
                    ...(intent.pin ? { pin: intent.pin } : {}),
                    ...(userAvatar ? { avatar: userAvatar } : {}),
                    ...(resumeToken ? { resume: resumeToken } : {})
                }
                : {
                    t: 'create',
                    nickname: intent.nickname,
                    ...(userAvatar ? { avatar: userAvatar } : {})
                })
            return
        }
        if (socketStatus === 'closed' && intent) setStatus('reconnecting')
        // Sem dependencias: o transporte deixou de ser derrubado aqui — quem
        // decide isso agora e o `joined`, comparando o id que voltou.
    }, [])

    /**
     * Outra aba sua assumiu o lugar. Esta aqui para de tentar e volta pra tela
     * inicial — insistir so tiraria a sala da aba que a pessoa esta usando.
     */
    const handleReplaced = useCallback(() => {
        clearActiveSession()
        intentRef.current = null
        desiredChannelRef.current = null
        clientRef.current = null
        teardownTransport()
        setRoom(null)
        setBans([])
        setMessages([])
        setSelfId(null)
        tokenRef.current = null
        selfIdRef.current = null
        setStatus('idle')
        setError({
            code: 'replaced',
            message: 'Voce abriu esta sala em outra aba. Esta ficou de fora.'
        })
    }, [teardownTransport])

    const ensureClient = useCallback(() => {
        if (clientRef.current) return clientRef.current
        const client = new SignalingClient({
            url: defaultSignalingUrl(),
            onMessage: handleMessage,
            onStatus: handleStatus,
            onReplaced: handleReplaced
        })
        clientRef.current = client
        client.connect()
        return client
    }, [handleMessage, handleStatus, handleReplaced])

    const enter = useCallback((nickname: string, code: string | null, pin?: string) => {
        const clean = nickname.trim()
        if (!clean) {
            setError({ code: 'invalid_nickname', message: 'Escolha um nickname.' })
            return
        }
        saveNickname(clean)
        setError(null)
        setStatus('connecting')
        intentRef.current = { code, nickname: clean, ...(pin ? { pin } : {}) }

        const client = ensureClient()
        const userAvatar = avatarRef.current || undefined
        const resumeToken = tokenRef.current || (code ? loadActiveSession(code) : null)
        const sent = client.send(code
            ? {
                t: 'join',
                code,
                nickname: clean,
                ...(pin ? { pin } : {}),
                ...(userAvatar ? { avatar: userAvatar } : {}),
                ...(resumeToken ? { resume: resumeToken } : {})
            }
            : { t: 'create', nickname: clean, ...(userAvatar ? { avatar: userAvatar } : {}) })
        // Socket ainda abrindo: o onStatus('open') reenvia a intencao.
        if (!sent) client.connect()
    }, [ensureClient])

    /**
     * Recarregou a pagina: volta pra sala sozinho, sem passar pela tela
     * inicial. O token guardado e a prova de que esta aba estava mesmo la
     * dentro — sem ele, abrir um link de sala continua pedindo o nickname.
     *
     * O `ref` e o que garante UMA entrada, e nao um efeito por render: `enter`
     * troca de identidade junto com a qualidade escolhida, e sem o guarda
     * mudar de 720p pra 1080p dentro da sala reenviava o `join`, trocava o id
     * e fazia a sala inteira refazer as conexoes P2P.
     */
    const entrouSozinho = useRef(false)
    useEffect(() => {
        if (typeof window === 'undefined' || entrouSozinho.current) return
        const code = roomCodeFromLocation(window.location.pathname, window.location.search)
        if (!code || !loadActiveSession(code)) return
        const nickname = loadNickname()
        if (!nickname) return
        entrouSozinho.current = true
        enter(nickname, code)
    }, [enter])

    const createRoom = useCallback((nickname: string) => enter(nickname, null), [enter])
    const joinRoom = useCallback(
        (code: string, nickname: string, pin?: string) => enter(nickname, code, pin),
        [enter]
    )

    /**
     * Encerra uma captura por inteiro: efeito, camera crua e stream publicada.
     * Sempre nesta ordem — o pipeline le frames da camera, e derrubar a fonte
     * antes deixaria o canvas desenhando quadro preto ate perceber.
     */
    const disposeCapture = useCallback((capture: LocalCapture) => {
        capture.pipeline?.stop()
        void capture.voice?.stop()
        stopStream(capture.raw)
        stopStream(capture.stream)
    }, [])

    /**
     * Aplica a lista nova de capturas, guarda no ref e publica.
     *
     * O orcamento vai antes da publicacao: trilha nova ja nasce com o teto do
     * tamanho dela, em vez de passar os primeiros segundos no da fonte anterior.
     */
    const applyCaptures = useCallback(async (next: LocalCapture[]) => {
        capturesRef.current = next
        setCaptures(next)
        transportRef.current?.setMaxBitrate(currentBudget())
        await publishAll(transportRef.current)
    }, [publishAll, currentBudget])

    /**
     * Para de transmitir. Sem argumento derruba tudo; com um tipo, so aquele —
     * desligar a camera nao pode levar a tela junto.
     */
    const stopSharing = useCallback((kind?: ShareKind) => {
        const current = capturesRef.current
        const going = kind ? current.filter((c) => c.kind === kind) : current
        if (going.length === 0) return

        for (const capture of going) disposeCapture(capture)
        void applyCaptures(kind ? current.filter((c) => c.kind !== kind) : [])
    }, [disposeCapture, applyCaptures])
    stopSharingRef.current = () => stopSharing()

    /**
     * Comeca (ou refaz) uma transmissao. As outras seguem no ar: tela e camera
     * convivem, cada uma no seu tile.
     */
    const startSharing = useCallback(async (
        kind: ShareKind = 'screen',
        nextQuality: ShareQuality = quality
    ) => {
        let stream: MediaStream
        // Quando o efeito roda por conta propria, a camera crua fica aqui e o
        // que vai pro ar e a saida do canvas — duas streams a fechar depois.
        let rawCamera: MediaStream | null = null
        let pipeline: BlurPipeline | null = null

        try {
            if (kind === 'camera') {
                const camera = await captureCamera(
                    nextQuality,
                    cameraIdRef.current ?? undefined,
                    resolutionRef.current
                )
                stream = camera

                const effect = backgroundRef.current
                if (effect.kind !== 'none') {
                    const track = camera.getVideoTracks()[0]
                    // So o desfoque tem equivalente no sistema; fundo por
                    // imagem sempre passa pelo nosso pipeline.
                    const nativo = effect.kind === 'blur' && track
                        ? await applyNativeBlur(track, true)
                        : false

                    if (!nativo) {
                        try {
                            pipeline = await createBlurPipeline(
                                camera,
                                QUALITY_PRESETS[nextQuality].frameRate,
                                effect
                            )
                            rawCamera = camera
                            stream = pipeline.stream
                        } catch (effectError) {
                            // Efeito e enfeite: se ele falhar, a camera entra
                            // sem fundo em vez de a pessoa ficar sem camera.
                            console.warn('[efeitos] fundo indisponivel', effectError)
                            setError({
                                code: 'capture_failed',
                                message: 'Nao consegui carregar o efeito; a camera entrou sem fundo.'
                            })
                        }
                    }
                }
            } else {
                stream = await captureScreen(nextQuality, resolutionRef.current)
            }
        } catch (captureError) {
            const denied = captureError instanceof DOMException
                && (captureError.name === 'NotAllowedError' || captureError.name === 'PermissionDeniedError')
            // Cancelar o seletor de tela cai aqui e nao e erro: a pessoa desistiu.
            // Ja a camera negada merece explicacao — o navegador lembra a escolha.
            if (kind === 'camera') {
                const notFound = captureError instanceof DOMException && captureError.name === 'NotFoundError'
                setError({
                    code: 'capture_failed',
                    message: notFound
                        ? 'Nenhuma camera encontrada neste dispositivo.'
                        : denied
                            ? 'Permissao de camera negada. Libere o acesso no cadeado da barra de endereco.'
                            : 'Nao consegui abrir a camera.'
                })
            } else if (!denied) {
                setError({ code: 'capture_failed', message: 'Nao consegui capturar a tela.' })
            }
            return
        }

        // A captura anterior DESTE tipo sai de cena inteira (stream publicada,
        // pipeline e camera crua); a do outro tipo segue intacta.
        const anterior = capturesRef.current.find((c) => c.kind === kind)
        if (anterior) disposeCapture(anterior)

        // Captura nova comeca com o som no ar: o corte anterior era daquela
        // tela, e carregar o estado pra frente faria a pessoa compartilhar
        // mudo sem entender por que.
        if (kind === 'screen') setScreenAudioMuted(false)

        const capture: LocalCapture = { kind, stream, raw: rawCamera, pipeline }
        const next = [...capturesRef.current.filter((c) => c.kind !== kind), capture]

        // O orcamento sai em `applyCaptures`, ja com o tamanho desta captura.
        setQuality(nextQuality)
        qualityRef.current = nextQuality

        /**
         * "Parar de compartilhar" na barra do proprio navegador: derruba so
         * esta transmissao.
         *
         * So as trilhas de VIDEO, que sao as que definem tela e camera. Com
         * `getTracks()` o audio da tela entrava junto, e ele acaba sozinho em
         * situacao normal — a guia capturada navega, a fonte de som e trocada,
         * o dispositivo de saida muda. Quando isso acontecia, a transmissao
         * INTEIRA caia: imagem, tile e tudo, sem ninguem ter pedido nada.
         */
        for (const track of stream.getVideoTracks()) {
            track.addEventListener('ended', () => {
                if (capturesRef.current.includes(capture)) stopSharing(kind)
            })
        }

        await applyCaptures(next)

        /**
         * A tela entrou sem som: avisa, porque nada mais avisa.
         *
         * `getDisplayMedia({ audio: true })` nao rejeita quando o navegador
         * nao pode dar audio — devolve so o video, calado. O unico sinal que
         * sobrava era o botao de cortar o som nao aparecer, e do outro lado o
         * icone de alto-falante tambem nao. Duas ausencias nao formam uma
         * explicacao, e o que chegava aqui era "compartilhei com audio e nao
         * funcionou", sem nada no console.
         */
        if (kind === 'screen'
            && capturesRef.current.includes(capture)
            && stream.getAudioTracks().length === 0) {
            setError({
                code: 'screen_no_audio',
                message: screenAudioMissingMessage(),
                severity: 'aviso'
            })
        }

        // No modo SFU, sem coordenadas ninguem consegue assinar — melhor avisar
        // do que deixar a pessoa achando que esta transmitindo.
        if (configRef.current.mode === 'sfu' && capturesRef.current.includes(capture)) {
            const anunciada = room?.participants
                .find((p) => p.id === selfId)?.publications
                .some((p) => p.kind === kind && p.sfu)
            if (anunciada === false) {
                setError({
                    code: 'capture_failed',
                    message: kind === 'camera'
                        ? 'Nao consegui publicar a camera no SFU.'
                        : 'Nao consegui publicar a tela no SFU.'
                })
            }
        }
    }, [quality, disposeCapture, applyCaptures, stopSharing, room, selfId])

    /**
     * Entra na voz. O microfone so e pedido aqui — quem entra na sala nao tem
     * o microfone aberto sem querer.
     */
    const startVoice = useCallback(async () => {
        if (capturesRef.current.some((c) => c.kind === 'voice')) return

        setVoiceLoading(true)
        let microphone: MediaStream
        try {
            microphone = await captureMicrophone(micIdRef.current ?? undefined)
        } catch (captureError) {
            const denied = captureError instanceof DOMException
                && (captureError.name === 'NotAllowedError' || captureError.name === 'PermissionDeniedError')
            const notFound = captureError instanceof DOMException && captureError.name === 'NotFoundError'
            setError({
                code: 'capture_failed',
                message: notFound
                    ? 'Nenhum microfone encontrado neste dispositivo.'
                    : denied
                        ? 'Permissao de microfone negada. Libere o acesso no cadeado da barra de endereco.'
                        : 'Nao consegui abrir o microfone.'
            })
            setVoiceLoading(false)
            return
        }

        let stream = microphone
        let raw: MediaStream | null = null
        let voice: VoicePipeline | null = null

        if (noiseSuppressionRef.current) {
            try {
                voice = await createVoicePipeline(microphone)
                raw = microphone
                stream = voice.stream
            } catch (voiceError) {
                // Supressao extra e melhoria: sem ela ainda resta a do
                // navegador, que ja segura ruido constante.
                console.warn('[voz] supressao por ML indisponivel', voiceError)
            }
        }

        // Entrou mudo? Respeita: abrir o microfone mandando audio sem querer e
        // o pior jeito de estrear numa sala.
        for (const track of stream.getAudioTracks()) track.enabled = !mutedRef.current

        const capture: LocalCapture = { kind: 'voice', stream, raw, pipeline: null, voice }
        await applyCaptures([...capturesRef.current.filter((c) => c.kind !== 'voice'), capture])
        setVoiceLoading(false)
    }, [applyCaptures])

    const stopVoice = useCallback(() => stopSharing('voice'), [stopSharing])

    /**
     * Identidade estavel de proposito: quem dispensa o aviso sozinho e um
     * `useEffect` com timer. Recriada a cada render, ela virava dependencia
     * nova toda vez — o timer era reiniciado antes de disparar e o aviso nunca
     * sumia.
     */
    const clearError = useCallback(() => setError(null), [])

    const setAudioState = useCallback((nextMuted: boolean, nextDeafened: boolean) => {
        clientRef.current?.send({ t: 'audio-state', muted: nextMuted, deafened: nextDeafened })
    }, [])

    /**
     * Silencia sem sair da voz: a trilha continua publicada, so para de
     * carregar audio. Sair e voltar renegociaria a conexao a cada clique.
     */
    const setMuted = useCallback((value: boolean) => {
        mutedRef.current = value
        setMutedState(value)
        const voice = capturesRef.current.find((c) => c.kind === 'voice')
        for (const track of voice?.stream.getAudioTracks() ?? []) track.enabled = !value
    }, [])

    const setMicId = useCallback((deviceId: string | null) => {
        micIdRef.current = deviceId
        setMicIdState(deviceId)
    }, [])

    /** Trocar de microfone ou de supressao reabre a captura no ar. */
    const refreshVoice = useCallback(async () => {
        if (!capturesRef.current.some((c) => c.kind === 'voice')) return
        stopSharing('voice')
        await startVoice()
    }, [stopSharing, startVoice])

    const setNoiseSuppression = useCallback(async (enabled: boolean) => {
        noiseSuppressionRef.current = enabled
        setNoiseSuppressionState(enabled)
        await refreshVoice()
    }, [refreshVoice])

    const changeMicrophone = useCallback(async (deviceId: string) => {
        setMicId(deviceId)
        await refreshVoice()
    }, [setMicId, refreshVoice])

    /**
     * Troca o fundo. Com a camera no ar, reabre a captura pra publicar o novo;
     * parada, so guarda a preferencia pra proxima vez que ela ligar.
     */
    const setBackground = useCallback(async (effect: BackgroundEffect) => {
        backgroundRef.current = effect
        setBackgroundState(effect)
        if (!capturesRef.current.some((c) => c.kind === 'camera')) return

        // Primeira vez baixa 11 MB de wasm: sem esse estado o seletor ficaria
        // parecendo quebrado por alguns segundos.
        setEffectLoading(true)
        try {
            await startSharing('camera')
        } finally {
            setEffectLoading(false)
        }
    }, [startSharing])

    const leaveRoom = useCallback(() => {
        clearActiveSession()
        stopSharing()
        intentRef.current = null
        desiredChannelRef.current = null
        clientRef.current?.send({ t: 'leave' })
        clientRef.current?.close()
        clientRef.current = null
        teardownTransport()
        setRoom(null)
        setBans([])
        setMessages([])
        setSelfId(null)
        tokenRef.current = null
        selfIdRef.current = null
        setStatus('idle')
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
            window.history.replaceState(null, '', '/')
        }
    }, [stopSharing, teardownTransport])

    /**
     * A aba esta indo embora (F5, link, fechar) — o que NAO e sair da sala.
     *
     * O `bye` avisa a sala na hora, e so o token fica pra tras: e ele que faz a
     * pagina nova reassumir o mesmo lugar. Ver `docs/retomada-de-sessao.md`.
     */
    const suspendSession = useCallback(() => {
        touchActiveSession()
        clientRef.current?.send({ t: 'bye' })
        clientRef.current?.close()
    }, [])

    /**
     * A pagina voltou inteira do cache do navegador — no celular, e o que
     * acontece toda vez que a pessoa troca de app e volta.
     *
     * O socket foi cortado enquanto ela dormia, e o backoff que tentaria
     * reconectar estava congelado junto. Sem este empurrao, voltar pro app
     * ficava esperando o proximo tique — ate 8 segundos olhando pra uma sala
     * que ja nao esta conectada.
     */
    const resumeSession = useCallback(() => {
        if (!intentRef.current) return
        clientRef.current?.connect()
    }, [])

    /**
     * O ciclo de vida da pagina, enquanto houver sala.
     *
     * `persisted` separa as duas saidas que o `pagehide` junta. Com ele ligado
     * a pagina vai INTEIRA pro cache do navegador e pode voltar do jeito que
     * estava — no celular e o que acontece a cada troca de app ou tela
     * bloqueada. Despedir-se ali tirava a pessoa da sala por olhar uma
     * notificacao.
     */
    useEffect(() => {
        if (typeof window === 'undefined' || !room) return
        const onHide = (event: PageTransitionEvent) => {
            if (!event.persisted) suspendSession()
        }
        const onShow = (event: PageTransitionEvent) => {
            if (event.persisted) resumeSession()
        }
        window.addEventListener('pagehide', onHide)
        window.addEventListener('pageshow', onShow)
        return () => {
            window.removeEventListener('pagehide', onHide)
            window.removeEventListener('pageshow', onShow)
        }
    }, [room, suspendSession, resumeSession])

    const rename = useCallback((nickname: string) => {
        const clean = nickname.trim()
        if (!clean) return
        saveNickname(clean)
        if (intentRef.current) intentRef.current.nickname = clean
        const userAvatar = avatarRef.current || undefined
        clientRef.current?.send({ t: 'rename', nickname: clean, ...(userAvatar ? { avatar: userAvatar } : {}) })
    }, [])

    const setAvatar = useCallback((dataUrl: string | null) => {
        avatarRef.current = dataUrl
        setAvatarState(dataUrl)
        if (dataUrl) {
            saveUserAvatar(dataUrl)
        } else {
            removeUserAvatar()
        }
        clientRef.current?.send({ t: 'avatar', avatar: dataUrl || undefined })
    }, [])

    const toggleScreenAudio = useCallback(() => {
        const tela = capturesRef.current.find((capture) => capture.kind === 'screen')
        if (!tela) return
        setScreenAudioMuted((atual) => {
            const proximo = !atual
            for (const track of tela.stream.getAudioTracks()) track.enabled = !proximo
            return proximo
        })
    }, [])

    const setCameraId = useCallback((deviceId: string | null) => {
        cameraIdRef.current = deviceId
        setCameraIdState(deviceId)
    }, [])

    const setLocked = useCallback((locked: boolean) => {
        clientRef.current?.send({ t: 'lock', locked })
    }, [])

    const kick = useCallback((id: string) => {
        clientRef.current?.send({ t: 'kick', id })
    }, [])

    /** Promove a moderador ou rebaixa. Quem pode o que, quem decide e o servidor. */
    const setAdmin = useCallback((id: string, admin: boolean) => {
        clientRef.current?.send({ t: 'promote', id, admin })
    }, [])

    /** Libera quem foi expulso: e o unico jeito de a pessoa voltar pelo link. */
    const unban = useCallback((id: string) => {
        clientRef.current?.send({ t: 'unban', id })
    }, [])

    /**
     * Manda no bate-papo. A mensagem so entra na tela quando o servidor a
     * devolve — assim todo mundo ve a mesma ordem, em vez de cada aba montar
     * a sua com a propria mensagem na frente.
     */
    const sendChat = useCallback((text: string) => {
        const clean = text.trim()
        if (!clean) return
        clientRef.current?.send({ t: 'chat', text: clean })
    }, [])

    /** Cria uma sub-sala. Quem pode e o nome que vale, quem decide e o servidor. */
    const createChannel = useCallback((name: string) => {
        const clean = name.trim()
        if (!clean) return
        clientRef.current?.send({ t: 'channel-create', name: clean })
    }, [])

    const deleteChannel = useCallback((id: string) => {
        clientRef.current?.send({ t: 'channel-delete', id })
    }, [])

    /**
     * Vai pra uma sub-sala (`null` = sala principal).
     *
     * O que estiver no ar vai junto: trocar de sub-sala e escolha de quem
     * troca, e quem esta la passa a ver. A troca so vale quando o servidor
     * confirma pelo `peer-update` — e ai a malha se refaz sozinha.
     */
    const joinChannel = useCallback((id: string | null) => {
        const code = intentRef.current?.code
        desiredChannelRef.current = id && code ? { code, channel: id } : null
        clientRef.current?.send({ t: 'channel-join', id })
    }, [])

    /**
     * Leva cadencia e teto de resolucao escolhidos pras trilhas NO AR.
     *
     * A tela muda por `applyConstraints`, sem recapturar: recapturar reabriria
     * o seletor do navegador, e escolher a janela de novo pra mexer num ajuste
     * de qualidade seria um preco alto demais. A camera com efeito de fundo e
     * a excecao — o canvas do pipeline nasce no tamanho e na cadencia da camera
     * e nao segue a trilha, entao ali so recomecar resolve; e nela nao ha
     * seletor nenhum pra atrapalhar.
     *
     * O orcamento sai depois que as trilhas assentam: em `nativa` e o tamanho
     * novo da fonte que diz quanta banda ela merece.
     */
    const retune = useCallback(async () => {
        const frameRate = { ideal: QUALITY_PRESETS[qualityRef.current].frameRate }
        const resolution = resolutionRef.current
        const pending: Promise<unknown>[] = []

        for (const capture of capturesRef.current) {
            if (capture.kind === 'camera' && capture.pipeline) {
                void startSharing('camera', qualityRef.current)
                continue
            }
            // `raw` e a camera de verdade quando ha pipeline; sem ele, a
            // propria stream publicada e que carrega a trilha da fonte.
            const source = capture.raw ?? capture.stream
            for (const track of source.getVideoTracks()) {
                // O teto sai da orientacao DESTA trilha: o mesmo preset aperta
                // lados diferentes numa fonte em pe e numa deitada.
                //
                // Cadencia e tamanho vao sempre juntos: `applyConstraints`
                // substitui o conjunto inteiro, e mandar so um apagaria o outro.
                pending.push(track.applyConstraints({
                    frameRate,
                    ...sizeConstraints(resolution, trackOrientation(track))
                }).catch(() => undefined))
            }
        }

        await Promise.all(pending)
        transportRef.current?.setMaxBitrate(currentBudget())
    }, [startSharing, currentBudget])

    /**
     * Troca a qualidade com a transmissao no ar.
     *
     * A cadencia vai pra trilha, e nao so pro menu: sem isso, quem comecava em
     * 30 fps e subia pra Fluido seguia capturando 30 — a etiqueta dizia
     * "Fluido" e o outro lado recebia metade dos quadros.
     */
    const applyQuality = useCallback((next: ShareQuality) => {
        setQuality(next)
        qualityRef.current = next
        const preset = QUALITY_PRESETS[next]

        for (const capture of capturesRef.current) {
            // Rosto pede fluidez: o preset "nitido" e pensado pra texto e
            // deixaria a camera travada em 15 fps com dica de conteudo errada.
            const hint = capture.kind === 'camera'
                ? (preset.contentHint === 'detail' ? 'motion' : (preset.contentHint || 'motion'))
                : preset.contentHint
            for (const track of capture.stream.getVideoTracks()) track.contentHint = hint
        }
        void retune()
    }, [retune])

    /** Escolha do menu: a partir dela a qualidade para de seguir o encoder. */
    const changeQuality = useCallback((next: ShareQuality) => {
        qualityChosenRef.current = true
        applyQuality(next)
    }, [applyQuality])

    /** Troca o teto de resolucao com a transmissao no ar. */
    const changeResolution = useCallback((next: ShareResolution) => {
        setResolution(next)
        resolutionRef.current = next
        void retune()
    }, [retune])

    /**
     * Leva pro ar o encoder que a escolha e a deteccao resolvem agora.
     *
     * Na malha o transporte troca o codec por dentro, sem renegociar. No SFU o
     * codec e fixado na publicacao, entao a tela e republicada — os outros
     * veem um piscar, e por isso so quando ha video no ar pra republicar.
     *
     * Sem qualidade escolhida no menu, a cadencia vem junto: 60 fps quando a
     * placa codifica, 30 no processador.
     */
    const applyEncoder = useCallback(() => {
        const next = resolveEncoder(encoderChoiceRef.current, gpuAvailableRef.current)
        if (next !== encoderRef.current) {
            setEncoderState(next)
            encoderRef.current = next
            // A medida anterior era do encoder antigo; a proxima rodada traz a nova.
            setEncoderInUse(null)

            const transport = transportRef.current
            if (transport) {
                transport.setEncoder(next)
                const temVideo = capturesRef.current.some((capture) => capture.stream.getVideoTracks().length > 0)
                if (configRef.current.mode === 'sfu' && temVideo) void publishAll(transport)
            }
        }
        if (!qualityChosenRef.current) {
            const quality = defaultQuality(next === 'gpu' && gpuAvailableRef.current === true)
            if (quality !== qualityRef.current) applyQuality(quality)
        }
    }, [publishAll, applyQuality])

    /** Troca quem codifica o video, com a transmissao no ar. */
    const changeEncoder = useCallback((next: EncoderChoice) => {
        setEncoderChoice(next)
        encoderChoiceRef.current = next
        saveEncoderChoice(next)
        applyEncoder()
    }, [applyEncoder])

    /**
     * Descobre de saida quais variantes de H.264 vao pra placa: a ordem dos
     * codecs da primeira transmissao ja sai dessa resposta, e ela chega bem
     * antes de alguem conseguir escolher uma tela.
     *
     * No automatico, a resposta tambem decide o encoder: placa que codifica
     * H.264 assume, e o processador fica livre.
     *
     * Pela ref, e nao pela dependencia: `applyEncoder` muda a cada troca de
     * qualidade ou de sala, e a deteccao so precisa rodar uma vez por aba.
     */
    const applyEncoderRef = useRef(applyEncoder)
    applyEncoderRef.current = applyEncoder
    useEffect(() => {
        let cancelled = false
        void detectHardwareEncoders().then((available) => {
            if (cancelled) return
            setGpuAvailable(available)
            gpuAvailableRef.current = available
            applyEncoderRef.current()
        })
        return () => { cancelled = true }
    }, [])

    const changeReceivePreference = useCallback((next: ReceivePreference) => {
        setReceivePreference(next)
        saveReceivePreference(next)
    }, [])

    // The same stream can be on stage and in PiP at once; the larger tile must win.
    const receiveRequests = useRef(new Map<string, Map<string, ReceiveLimit | null>>())
    /** `limit === undefined` withdraws this tile's request. */
    const requestReceiveLimit = useCallback((
        requester: string,
        peerId: string,
        streamId: string,
        limit: ReceiveLimit | null | undefined
    ) => {
        const key = streamKey(peerId, streamId)
        const requests = receiveRequests.current.get(key) ?? new Map<string, ReceiveLimit | null>()
        if (limit === undefined) requests.delete(requester)
        else requests.set(requester, limit)
        if (requests.size === 0) {
            receiveRequests.current.delete(key)
            return
        }
        receiveRequests.current.set(key, requests)
        transportRef.current?.requestReceiveLimit(peerId, streamId, loosestLimit([...requests.values()]))
    }, [])

    /** Ha alguma midia no ar? Sem isso nao ha conexao de midia pra medir. */
    const hasMedia = Object.keys(streams).length > 0 || captures.length > 0

    /**
     * Mede a banda de tempos em tempos.
     *
     * So a banda: a cadencia sai do proprio `<video>`, no tile, porque ela
     * existe mesmo sem conexao nenhuma — sozinho numa sala em modo mesh nao ha
     * `RTCPeerConnection` pra `getStats()` ler, e um fps tirado daqui nunca
     * apareceria pra quem esta conferindo a propria tela.
     *
     * So roda quando o servidor liga (`STREAM_STATS`) e quando ha algo pra
     * medir. `getStats()` e barato por chamada, mas sao varias por rodada numa
     * sala cheia — a chave existe justamente pra desligar isto sem deploy se o
     * custo aparecer.
     */
    useEffect(() => {
        if (!config.streamStats) {
            setBitrates((current) => (Object.keys(current).length === 0 ? current : {}))
            setEncoderInUse(null)
            return
        }
        if (!hasMedia) {
            setEncoderInUse(null)
            return
        }

        // Contadores sao acumulados: a taxa sai da diferenca entre duas
        // leituras, entao a primeira rodada so estabelece a base.
        let previous: Map<string, TrackSample> | null = null
        let cancelled = false

        const medir = async () => {
            const transport = transportRef.current
            if (!transport) return
            const current = await transport.collectStats()
            if (cancelled) return

            // So o video que EU envio: e nele que mora a escolha de encoder.
            const minhas = new Set(capturesRef.current.flatMap(
                (capture) => capture.stream.getVideoTracks().map((track) => track.id)
            ))
            let hardware: boolean | undefined
            for (const [trackId, sample] of current) {
                if (!minhas.has(trackId) || sample.hardware === undefined) continue
                hardware = (hardware ?? true) && sample.hardware
            }
            setEncoderInUse(hardware === undefined ? null : hardware ? 'gpu' : 'cpu')

            if (previous) {
                const rates: Record<string, number> = {}
                for (const [trackId, sample] of current) {
                    const antes = previous.get(trackId)
                    const rate = antes ? bitrateBetween(antes, sample) : null
                    if (rate !== null) rates[trackId] = rate
                }
                // So troca o estado quando algum numero mudou: um objeto novo a
                // cada rodada re-renderizava a sala inteira a cada 2 s.
                setBitrates((atual) => (sameRates(atual, rates) ? atual : rates))
            }
            previous = current
        }

        void medir()
        const timer = window.setInterval(() => void medir(), STATS_INTERVAL_MS)
        return () => {
            cancelled = true
            window.clearInterval(timer)
        }
    }, [config.streamStats, hasMedia])

    useEffect(() => () => {
        for (const capture of capturesRef.current) {
            capture.pipeline?.stop()
            stopStream(capture.raw)
            stopStream(capture.stream)
        }
        transportRef.current?.close()
        clientRef.current?.close()
    }, [])

    const self = useMemo<Participant | null>(
        () => room?.participants.find((p) => p.id === selfId) ?? null,
        [room, selfId]
    )

    /** Sub-sala em que estou (`null` = sala principal). */
    const selfChannel = self?.channel ?? null
    useEffect(() => {
        selfChannelRef.current = selfChannel
    }, [selfChannel])

    /** Tipos no ar agora — o que a barra de controles usa pra marcar os botoes. */
    const sharingKinds = useMemo(
        () => new Set(captures.map((capture) => capture.kind)),
        [captures]
    )

    /** Sua trilha de voz, quando ha — usada pelo medidor do proprio nivel. */
    const voiceStream = useMemo(
        () => captures.find((capture) => capture.kind === 'voice')?.stream ?? null,
        [captures]
    )

    return {
        status,
        selfId,
        self,
        room,
        streams,
        /** Conexao de midia por peer — quem esta preto e por que. */
        peerStates,
        captures,
        sharingKinds,
        screenAudioMuted,
        /** A tela no ar tem som? Sem isso o botao de cortar nao faz sentido. */
        screenHasAudio: captures.some((capture) => capture.kind === 'screen'
            && capture.stream.getAudioTracks().length > 0),
        toggleScreenAudio,
        cameraId,
        background,
        effectLoading,
        muted,
        micId,
        noiseSuppression,
        voiceLoading,
        voiceStream,
        inVoice: captures.some((capture) => capture.kind === 'voice'),
        quality,
        resolution,
        encoder,
        encoderChoice,
        encoderInUse,
        gpuAvailable,
        bitrates,
        /** O servidor liga ou desliga a medicao de cadencia e banda. */
        statsEnabled: config.streamStats,
        error,
        config,
        isSharing: captures.length > 0,
        createRoom,
        joinRoom,
        leaveRoom,
        suspendSession,
        resumeSession,
        avatar,
        setAvatar,
        rename,
        setCameraId,
        setBackground,
        startVoice,
        stopVoice,
        setMuted,
        setAudioState,
        setNoiseSuppression,
        changeMicrophone,
        setLocked,
        kick,
        setAdmin,
        unban,
        bans,
        messages,
        historyVersion,
        sendChat,
        /** Sub-salas da sala fixa. `undefined` na sala do momento, que nao tem. */
        channels: room?.channels,
        selfChannel,
        createChannel,
        deleteChannel,
        joinChannel,
        startSharing,
        stopSharing,
        changeQuality,
        changeResolution,
        changeEncoder,
        receivePreference,
        changeReceivePreference,
        requestReceiveLimit,
        clearError
    }
}

export type RoomSession = ReturnType<typeof useRoomSession>
